import { AboutModalProps } from '../models';
import {ReactComponent as DeleteIcon} from '../../../images/nde-icon-delete.svg';
import './AboutModal.scss';
import ReactModal from 'react-modal';
import { useSelector } from 'react-redux';
import { selectControls } from '../../../redux/controls';
import { SyntheticEvent, useCallback, useRef } from 'react';

const COPYRIGHT_ID = 'copyrightpolicy';
const COPYRIGHT_HASH = '#' + COPYRIGHT_ID;


export const AboutModal = ({open, onClose, goToCopyright}: AboutModalProps): JSX.Element => {
  const cancelEl = useRef<HTMLButtonElement>(null);

  const controls = useSelector(selectControls);

  const onAfterOpen = useCallback((): void => {
    if (goToCopyright) {
      const foundEl = document.querySelector(COPYRIGHT_HASH);
      if (foundEl) {
        foundEl.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'start'
        });
      }
      cancelEl.current?.focus();
    } else {
      cancelEl.current?.focus();
    }
  }, [goToCopyright]);

  const onClickAnchorHash = useCallback((event: SyntheticEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const selector = event.currentTarget.hash;
    const foundEl = document.querySelector(selector);
    if (foundEl) {
      foundEl.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'start'
      });
    }
  }, []);

  return (
    <>
      {
        open &&
        <ReactModal
          isOpen={open}
          className="nqt-modal nqt-about-modal"
          onAfterOpen={() => onAfterOpen()}
          overlayClassName="nqt-modal-overlay"
          shouldFocusAfterRender={true}
          shouldReturnFocusAfterClose={true}
          contentLabel={controls?.FooterAboutPUTitle?.controlText}
        >
          <div className='nqt-modal--container'>
            <div className="nqt-modal--header-group">
              <header className='nqt-modal--title'>{controls?.FooterAboutPUTitle?.controlText}</header>
              <button className='nqt-modal--close-button'
                ref={cancelEl}
                onClick={() => onClose(false)}>
                <DeleteIcon/>
                <span className="visually-hidden">{controls?.ModalClose?.controlText}</span>
              </button>
            </div>
            <div className="nqt-modal--border">
              <section className="nqt-modal--content">
                <div style={{padding: '10px'}}>
                  <strong>About the NAEP Questions Tool (NQT)</strong>
                  <br />
                  <ul style={{padding: '20px'}}>
                    <li><a href="#whatisthenaepquestiontool" onClick={onClickAnchorHash} title="What is the NAEP Questions Tool">What is the NAEP Questions Tool</a>?</li>
                    <li><a href="#releaseofnaepquestions" onClick={onClickAnchorHash} title="Release of NAEP Questions">Release of NAEP Questions</a></li>
                    <li><a href="#hownaepquestionsareused" onClick={onClickAnchorHash} title="How NAEP Questions Are Used">How NAEP Questions Are Used</a></li>
                    <li><a href={COPYRIGHT_HASH} onClick={onClickAnchorHash} title="Copyright Policy">Copyright Policy</a></li>
                    <li><a href="#questiondifficulty" onClick={onClickAnchorHash} title="Question Difficulty">Question Difficulty</a></li>
                    <li><a href="#percentcorrect" onClick={onClickAnchorHash} title="Percent Correct">Percent Correct</a></li>
                    <li><a href="#scoringguides" onClick={onClickAnchorHash} title="Scoring Guides">Scoring Guides</a></li>
                    <li><a href="#studentsampleresponses" onClick={onClickAnchorHash} title="Student Sample Responses">Student Sample Responses</a></li>
                  </ul>
                  <br />
                  <strong><a id="whatisthenaepquestiontool"></a>What is the NAEP Questions Tool?</strong><br />
            Each time the National Assessment of Educational Progress (NAEP) releases the results of a subject-level assessment, a portion of the questions used in the assessment is released to the public via the NAEP Questions Tool (NQT). The NQT provides access to more than 3,000 released questions from NAEP assessments across all NAEP subject areas. Users can search questions, look at performance data, and test themselves using NAEP questions. The tool also allows users to create their own customized assessments, create rosters, and save tests for future use.
                  <p></p>
                  <strong><a id="releaseofnaepquestions"></a>Release of NAEP questions</strong><br />
            The release of NAEP questions from completed assessments to the general public is an integral part of the NAEP reporting process.  Individual questions selected for release align with the framework for each subject’s assessment, but represent only a subset of questions on the assessment.  This subset of questions does not provide a complete representation of a framework.  <i>Note:  The NAEP frameworks describe the theoretical bases for assessments and describe the questions that should be included and how they should be designed and scored.  The <a href="http://nces.ed.gov/nationsreportcard/about/nagb/" title="National Assessment Governing Board">National Assessment Governing Board</a> develops the NAEP assessment frameworks.</i>
                  <p>
                The year listed with each question is the year in which it was released to the public.
                  </p>
                  <p></p>
                  <strong><a id="hownaepquestionsareused"></a>How NAEP Questions Are Used</strong><br />
            The questions in the NQT are provided as
                  <br />
                  <ul style={{padding: '20px'}}>
                    <li>examples of what NAEP asks students at grades 4, 8, and 12 for main NAEP, and at ages 9, 13, and 17 for long-term trend NAEP;</li>
                    <li>exemplars of questions that probe students&apos; knowledge of a specific content area; and</li>
                    <li>a way to compare an individual&apos;s performance on a specific question to that of students across the nation and in each participating state/jurisdiction.</li>
                  </ul>

            Because a certain number of questions must be kept secure, released questions in this tool do not represent complete coverage of the content, cognitive skills, and range of difficulty in the NAEP assessment for a particular subject area. These questions are not intended to be used as practice tests for future NAEP assessments.
                  <p></p>
                  <strong><a id={COPYRIGHT_ID}></a>Copyright Policy</strong><br />

            Material contained in the NAEP Questions Tool is from the National Assessment of Educational Progress (NAEP). This material is in the public domain (excluding any third-party copyrighted material it may contain), and permission is therefore not required to reproduce it. Users of NAEP items that include third-party copyrighted materials, e.g., reading passages, photographs, images, etc., must seek and receive copyright permission from the copyright holder before that material is reproduced elsewhere. Material that is copyrighted contains a citation line specifying the owner of the content. You must contact the copyright holder to use this material on other sites.
                  <br />
                  <p>Although all other material in the NQT is in the public domain and permission is not required to reproduce it, please print an acknowledgment of its source. You are encouraged to reproduce this material as needed. If you publish any part of the questions, please include the acknowledgment below. The year and the name of the assessment you are using (e.g., 2013 reading) should appear at the end of the statement.</p>

            SOURCE: U.S. Department of Education, Institute of Education Sciences, National Center for Education Statistics, National Assessment of Educational Progress (NAEP), 2013 Reading Assessment.
                  <p></p>
                  <strong><a id="questiondifficulty"></a>Question Difficulty</strong><br />

            For multiple-choice and dichotomously scored (right-wrong) questions, easy questions are those with scores greater than 60 percent, medium questions are those with scores greater than or equal to 40 percent and less than 60 percent, and hard questions are those with scores less than 40 percent. For constructed-response (open-ended) questions, separate weights are assigned to each score category.
                  <p></p>
                  <strong><a id="percentcorrect"></a>Percent Correct</strong><br />

            The data in the NQT show the percent of students who answered a particular question correctly. For a constructed-response question in which students could earn partial or complete credit, the percent correct is computed by adding the percent of students receiving full credit to a fraction of the percent of the students receiving partial credit.
                  <p>For example, some questions are scored correct, partial, or incorrect. If, for example, 16 percent of the students gave a fully correct answer on a question, and an additional 24 percent of the students gave a partial answer, the percent correct for this question would be computed as 16 + 1/2 (24) = 28. The partial results were weighted by 1/2 because there were two levels of credit for the question. Responses to a question with four levels of credit would receive weights of 1/4 (minimal), 1/2 (partial), and 3/4 (satisfactory).</p>
                  <p></p>
                  <strong><a id="scoringguides"></a>Scoring Guides</strong>
                  <br />
            The tool presents the scoring guides that reflect the scoring criteria used by trained scorers. The guides were intended to be used in face-to-face training of scorers rather than as stand-alone documents. Scoring guides provide NQT users with an idea of what was considered by scorers when items were scored.
                  <p>
                The guides in the NQT match the content and intent of the scoring guides that are used by the scorers; however, miscellaneous notes and unnecessary detail are not included. The guides in the NQT, like the NQT itself, are intended for a general audience. For inquiries about scoring guides for individual questions, please go to <a href="http://nces.ed.gov/nationsreportcard/contactus.asp" title="Contact Us">Contact Us</a> and submit your requests.
                  </p>

                  <strong><a id="studentsampleresponses"></a>Sample Student Responses (for Constructed-Response Items)</strong>

                  <ul style={{padding: '20px'}}>
                    <li>If available, two student responses for each score level are provided in the NQT. The responses are representative of the score level.</li>
                    <li>Each pair of student responses is labeled with the correct score level according to the scoring guide.</li>
                    <li>Student spelling and wording are not changed because the intent is to show exactly how a student responded to an item.</li>
                    <li>References to specific locations or people, e.g., a student&apos;s school, are removed. No information that would identify a student or their location is included in a response.</li>
                  </ul>
                  <p><span style={{color: 'brown'}}><i><strong>We need your help</strong> to make the NAEP Questions Tool better! Please</i></span> <a href="http://nces.ed.gov/nationsreportcard/contactus.asp" title="send us your comments and suggestions">send us your comments and suggestions</a>.</p>
                </div>
              </section>
            </div>
          </div>
        </ReactModal>
      }
    </>
  );
};

export default AboutModal;